import * as Sentry from '@sentry/browser';
import {
  validatePhoneNumber,
  validatePostalCode,
  validateAccountNumber,
  validateAddress,
  formatSIN,
  validateSIN,
} from 'utils/validators';

export const valueNullValidation = (text) => {
  if (text) {
    return {
      isValid: true,
      errorMessage: '',
    };
  }
  return {
    isValid: false,
    errorMessage: 'Field cannot be empty',
  };
};

export const checkTextInput = (value) => {
  const regex = '^[A-Za-z ]*$';
  if (value) {
    return removeSpecialCharacters(value);
  }
};

export const checkPostalCode = (value) => {
  const regex = /^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/;
  if (!regex.test(value)) {
    return 'Postal Code must be in the format A1A 1A1';
  }
  return null;
};

function removeSpecialCharacters(str) {
  return str.replace(/[^A-Za-z ]/g, '');
}

export const checkAlphanumeric = (value) => {
  if (value) {
    return value.replace(/[^a-zA-Z0-9]/g, '');
  }
};

// Input filter for street addresses: keeps letters (incl. accented),
// digits, spaces and , . ' # / - as the user types. Unlike checkTextInput
// this must NOT strip digits — street numbers are part of the address.
export const checkAddressInput = (value) => {
  if (value) {
    return String(value).replace(/[^A-Za-z0-9À-ÿ\s,.'#/-]/g, '');
  }
};

// Save-time validation: address must not contain special characters.
// Emptiness is handled by the type-default required check that
// mergeValidations appends, so an empty value passes here.
export const checkAddressValidation = (text) => {
  if (!text) {
    return {
      isValid: true,
      errorMessage: '',
    };
  }
  if (validateAddress(String(text))) {
    return {
      isValid: true,
      errorMessage: '',
    };
  }
  return {
    isValid: false,
    errorMessage: 'Address cannot contain special characters',
  };
};

// Input filter: digits only (same convention as checkAlphanumeric).
export const checkNumericInput = (value) => {
  if (value) {
    return String(value).replace(/[^0-9]/g, '');
  }
};

// Input filter for SIN: digits only, dashed into the Canadian 3-3-3
// grouping (123-456-789) as the user types. Previously grouped 3-2-4
// (US SSN style) — Canadian SINs are three groups of three.
export const checkSSN = (input) => formatSIN(input);

// Save-time validation: exactly 9 digits (separators ignored). Plugged into field
// configs via checkValidation so checkValidations() surfaces it inline.
export const checkSINValidation = (text) => {
  if (!text) {
    return {
      isValid: false,
      errorMessage: 'Field cannot be empty',
    };
  }
  if (!validateSIN(String(text))) {
    return {
      isValid: false,
      errorMessage: 'Enter a valid 9-digit SIN (e.g. 123-456-789)',
    };
  }
  return {
    isValid: true,
    errorMessage: '',
  };
};

// Save-time guard for future-only date fields (Closing/Renewal, NOF). The
// picker already disables past days in the calendar (disablePastDate), but
// a manually typed past date must surface an inline message rather than be
// silently accepted or snapped back (BAT1 #7/#8). Today passes; empty
// values pass (the fields are optional).
export const checkNotPastDate = (value) => {
  if (!value) {
    return { isValid: true, errorMessage: '' };
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return { isValid: false, errorMessage: 'Enter a valid date' };
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const candidate = new Date(date.getTime());
  candidate.setHours(0, 0, 0, 0);
  if (candidate < today) {
    return { isValid: false, errorMessage: 'Date cannot be in the past' };
  }
  return { isValid: true, errorMessage: '' };
};

export const validatePercentageInput = (event) => {
  const value = event.target.value;
  if (value) {
    const regex = /^\d{0,6}(\.\d{0,3})?%?$/;
    return value.replace(regex, '');
  }

  // Update the input value with the validated value
  return value;
};

export const numberValidation = (text) => {
  if (parseFloat(text) === 0 || !text) {
    return {
      isValid: false,
      errorMessage: 'Field cannot be empty or 0',
    };
  } else if (isNaN(+text)) {
    return {
      isValid: false,
      errorMessage: 'Enter valid value',
    };
  } else if (
    String(text)?.includes('-') ||
    String(text)?.includes('e') ||
    String(text)?.includes('+')
  ) {
    return {
      isValid: false,
      errorMessage: 'Enter valid number',
    };
  }
  return {
    isValid: true,
    errorMessage: '',
  };
};

export const isLessThanZeroValidation = (text) => {
  if (parseFloat(text) < 0) {
    return {
      isValid: false,
      errorMessage: 'Value should not be less than 0',
    };
  }
  return {
    isValid: true,
    errorMessage: '',
  };
};

export const checkPhoneNumberValidation = (text) => {
  try {
    if (text) {
      if (
        (typeof text === 'string' || typeof text === 'number') &&
        String(text)?.match(/\d/g).length === 10
      ) {
        return {
          isValid: true,
          errorMessage: '',
        };
      }
      return {
        isValid: false,
        errorMessage: 'Enter valid Phone Number',
      };
    }
    return {
      isValid: false,
      errorMessage: 'Field cannot be empty',
    };
  } catch (error) {
    Sentry.captureException(error);

    return {
      isValid: false,
      errorMessage: 'Field cannot be empty',
    };
  }
};

// Form-schema adapter for validatePhoneNumber with the PROD-549 error copy
// (10 digits, or 11-15 digits with a "+" country code). checkPhoneNumberValidation
// above is kept as-is for its other consumers (leads, admin invite forms).
export const checkMobileNumberValidation = (text) => {
  if (!text) {
    return {
      isValid: false,
      errorMessage: 'Field cannot be empty',
    };
  }
  if (!validatePhoneNumber(String(text))) {
    return {
      isValid: false,
      errorMessage:
        'Enter a valid mobile number (10 digits, or max 15 characters with country code)',
    };
  }
  return {
    isValid: true,
    errorMessage: '',
  };
};

// Numeric upper-limit validator (PROD-552). Reads maxValue from the field
// config so the same function serves any capped field; pair with
// validateWhenFilled on optional fields.
export const checkMaxValueValidation = (text, config) => {
  const max = config?.maxValue;
  if (max == null || text === '' || text == null) {
    return { isValid: true, errorMessage: '' };
  }
  const num = parseFloat(text);
  if (!Number.isNaN(num) && num > max) {
    return {
      isValid: false,
      errorMessage: `Value must not exceed ${max}`,
    };
  }
  return { isValid: true, errorMessage: '' };
};

// Form-schema adapter for validateAccountNumber (PROD-550): numeric only,
// max 15 digits. Pair with validateWhenFilled on optional fields.
export const checkAccountNumberValidation = (text) => {
  if (!text) {
    return {
      isValid: false,
      errorMessage: 'Field cannot be empty',
    };
  }
  if (!validateAccountNumber(String(text))) {
    return {
      isValid: false,
      errorMessage: 'Enter a valid account number (max 15 digits)',
    };
  }
  return {
    isValid: true,
    errorMessage: '',
  };
};

// Form-schema adapter for validatePostalCode; plugged into field configs via
// checkValidation so checkValidations() surfaces the error inline (PROD-546).
export const checkPostalCodeValidation = (text) => {
  if (!text) {
    return {
      isValid: false,
      errorMessage: 'Field cannot be empty',
    };
  }
  if (!validatePostalCode(String(text))) {
    return {
      isValid: false,
      errorMessage: 'Enter a valid postal code (e.g. A1A 1A1)',
    };
  }
  return {
    isValid: true,
    errorMessage: '',
  };
};

export const checkEmailValidation = (text) => {
  try {
    if (text) {
      // Updated regex to disallow spaces
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (
        typeof text === 'string' &&
        emailRegex.test(text) &&
        !/\s/.test(text)
      ) {
        return {
          isValid: true,
          errorMessage: '',
        };
      }
      return {
        isValid: false,
        errorMessage: 'Enter a valid Email Address without spaces',
      };
    }
    return {
      isValid: false,
      errorMessage: 'Field cannot be empty',
    };
  } catch (error) {
    Sentry.captureException(error);

    return {
      isValid: false,
      errorMessage: 'Field cannot be empty',
    };
  }
};

export const selectValidation = (data) => {
  if (data?.id) {
    return {
      isValid: true,
      errorMessage: '',
    };
  }
  return {
    isValid: false,
    errorMessage: 'Field cannot be empty',
  };
};

export const createFormObject = (data) => {
  return data?.reduce((acc, value) => {
    if (value.type === 'container') {
      const formFields = value.formFields?.reduce(
        (acc, value) => ({ ...acc, [value.propertyName]: '' }),
        {}
      );
      return { ...acc, ...formFields };
    }
    return { ...acc, [value.propertyName]: '' };
  }, {});
};

export const checkMinAndMax = (value, config) => {
  if (config?.min && config?.max) {
    const isValid =
      parseFloat(value) <= config?.max && parseFloat(value) >= config?.min;
    return {
      isValid,
      errorMessage: isValid
        ? ''
        : `${value} should be between ${config?.min} and ${config?.max}`,
    };
  }
  if (config?.min) {
    const isValid = parseFloat(value) <= config?.min;
    return {
      isValid,
      errorMessage: isValid
        ? ''
        : `${value} should be greater than ${config?.min}`,
    };
  }

  if (config?.max) {
    const isValid = parseFloat(value) <= config?.max;
    return {
      isValid,
      errorMessage: isValid
        ? ''
        : `${value} should be less than ${config?.max}`,
    };
  }
  return {
    isValid: true,
    errorMessage: '',
  };
};

function applyValidations(obj) {
  switch (obj.type) {
    case 'text':
      return [valueNullValidation];
    case 'number':
      return [
        valueNullValidation,
        numberValidation,
        isLessThanZeroValidation,
        checkMinAndMax,
      ];
    case 'select':
      return [selectValidation];
    case 'checkbox':
      return [valueNullValidation];
    default:
      return [valueNullValidation];
  }
}

function applyValidationsToFields(fields) {
  return fields.map((field) => ({
    ...field,
    checkValidation: mergeValidations(field),
  }));
}

// validateWhenFilled fields are optional-by-definition: keep only their own
// validators — the type defaults (required/empty/zero checks) would wrongly
// flag legitimate values like a garage-space count of 0.
function mergeValidations(obj) {
  if (!Array.isArray(obj?.checkValidation)) {
    return applyValidations(obj);
  }
  if (obj.validateWhenFilled) {
    return [...obj.checkValidation];
  }
  return [...obj.checkValidation, ...applyValidations(obj)];
}

// Function to add validations based on type
export const withValidations = (array) => {
  return array.map((obj) => {
    if (obj.type === 'container' && obj.formFields) {
      return {
        ...obj,
        formFields: applyValidationsToFields(obj.formFields),
      };
    } else {
      return {
        ...obj,
        checkValidation: mergeValidations(obj),
      };
    }
  });
};

function transformErrorsToObject(errors) {
  const errorObj = {};

  errors.forEach((error) => {
    if (!(error.fieldName in errorObj)) {
      errorObj[error.fieldName] = error.errorMessage;
    }
  });

  return errorObj;
}

// Scroll to and focus a DynamicForm field by its propertyName. Deferred so
// any tab/section/accordion switch has re-rendered first; DynamicForm gives
// inputs id="form-field-<propertyName>". Fields whose id lands on a
// non-focusable container (e.g. react-select) still get scrolled on screen.
// options.scope: CSS selector for the container to search within — needed on
// entry-list forms where the same field ids repeat across entries.
export const focusFormField = (fieldKey, options = {}) => {
  if (!fieldKey) return;
  const { scope, delay = 150 } = options;
  setTimeout(() => {
    const root = (scope && document.querySelector(scope)) || document;
    const el =
      root.querySelector(`[id="form-field-${fieldKey}"]`) ||
      root.querySelector(`[name="${fieldKey}"]`);
    if (!el) {
      // Field not addressable (e.g. a custom select without form-field id):
      // at least bring its entry card on screen.
      if (scope && root !== document) {
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      return;
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (typeof el.focus === 'function') {
      el.focus({ preventScroll: true });
    }
  }, delay);
};

// Entry-list companion for forms that validate per-entry (employment,
// assets, liabilities, REO) and key errors as { `<prefix>_<index>`: {field:
// msg} } (plus flat groups like `otherAssets`). Expands the failing entry
// via onExpandIndex, then focuses its first invalid field scoped to the
// card tagged data-entry-card="<entryKey>".
export const focusFirstEntryError = (errorsByEntry, onExpandIndex) => {
  const firstEntryKey = Object.keys(errorsByEntry || {})[0];
  if (!firstEntryKey) return;
  const fieldKey = Object.keys(errorsByEntry[firstEntryKey] || {})[0];
  const indexMatch = /_(\d+)$/.exec(firstEntryKey);
  if (indexMatch && typeof onExpandIndex === 'function') {
    onExpandIndex(Number(indexMatch[1]));
  }
  focusFormField(fieldKey, {
    scope: indexMatch ? `[data-entry-card="${firstEntryKey}"]` : undefined,
  });
};

export const checkValidations = (array, formData, additionalInfo) => {
  const errors = [];

  array.forEach((obj) => {
    let fieldValue, validationFunctions;

    // Determine the validation function to use
    if (
      (typeof obj.isRequired === 'function' &&
        obj.isRequired(formData, additionalInfo)) ||
      (typeof obj.isRequired !== 'function' && obj.isRequired) ||
      obj.type === 'container'
    ) {
      if (obj.type === 'container' && obj.formFields) {
        obj.formFields.forEach((field) => {
          fieldValue = formData[field.propertyName];
          validationFunctions = field.checkValidation;
          if (
            (typeof field.isRequired === 'function' &&
              field.isRequired(formData, additionalInfo)) ||
            (typeof field.isRequired !== 'function' && field.isRequired)
          ) {
            validateField(
              field.propertyName,
              fieldValue,
              validationFunctions,
              field
            );
          }
        });
      } else {
        fieldValue = formData[obj.propertyName];
        validationFunctions = obj.checkValidation;
        validateField(obj.propertyName, fieldValue, validationFunctions, obj);
      }
    } else if (obj.validateWhenFilled && formData[obj.propertyName]) {
      // Optional fields opt in via validateWhenFilled: validators run only
      // when the user actually entered something (PROD-546 — REO postal code).
      validateField(
        obj.propertyName,
        formData[obj.propertyName],
        obj.checkValidation,
        obj
      );
    }
  });

  return transformErrorsToObject(errors);

  // Function to validate a field and push error if validation fails
  function validateField(
    fieldName,
    fieldValue,
    validationFunctions,
    fieldConfig
  ) {
    validationFunctions.forEach((validationFnc, index) => {
      if (validationFnc) {
        const validationResult = validationFnc(fieldValue, fieldConfig);
        if (!validationResult.isValid) {
          errors.push({
            fieldName: fieldName,
            errorMessage: validationResult.errorMessage,
          });
        }
      }
    });
  }
};

export const formatData = (data, property, options = {}) => {
  const fallbackNameKey = property
    ? property.replace(/Id$/i, 'Name')
    : undefined;
  const nameKey = options?.nameKey || fallbackNameKey;
  const source = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
      ? data.data
      : [];

  return source.map((item) => {
    const id =
      item?.[property] ?? item?.id ?? item?.value ?? item?.key ?? null;
    const name =
      item?.name ??
      item?.label ??
      item?.display_name ??
      item?.displayName ??
      (nameKey ? item?.[nameKey] : undefined) ??
      item?.code ??
      (id !== null && id !== undefined ? String(id) : '');

    return {
      ...item,
      id,
      name,
    };
  });
};

export function removeEmptyStrings(obj) {
  const newObj = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== '') {
      newObj[key] = value;
    }
  }
  return newObj;
}

export function formatPhoneNumber(number, format) {
  // Remove all non-digits from the number
  const cleaned = ('' + number).replace(/\D/g, '');

  let index = 0;
  let formattedNumber = '';

  // Iterate over the format and replace each # with a digit from the number
  for (let i = 0; i < format.length; i++) {
    if (format[i] === '#') {
      if (index < cleaned.length) {
        formattedNumber += cleaned[index];
        index++;
      } else {
        formattedNumber += '#'; // If number is shorter than format, replace with #
      }
    } else {
      formattedNumber += format[i]; // Keep non-# characters from the format
    }
  }

  return formattedNumber;
}
